/**
 * Phase 64 (Plan 64-05) — Training compliance hooks.
 *
 * TanStack Query wrappers over `services/api/training`, mirroring `useKudos.ts`:
 * stable queryKeys, JIT staleTime for the Dojo catalogue, a course-gated matrix
 * query, and requirement mutations that invalidate the requirements list on
 * success.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMyTrainings,
  getAvailableCourses,
  syncCatalog,
  getTrainingComplianceMatrix,
  listRequirements,
  createRequirement,
  updateRequirement,
  deleteRequirement,
  downloadTrainingEvidencePdf,
  type ComplianceStatusFilter,
  type RequirementCreateInput,
  type RequirementUpdateInput,
  type TrainingEvidencePdfParams,
} from '../services/api/training';

const TRAININGS_KEY = 'trainings';

export function useMyTrainings(enabled = true) {
  return useQuery({
    queryKey: [TRAININGS_KEY, 'my'],
    queryFn: getMyTrainings,
    // Always refetch on mount: a pass earned in Dojo arrives out-of-band (webhook
    // + hourly reconcile), so returning to this page should show current status,
    // not the app's 5-min-cached snapshot.
    staleTime: 0,
    // Phase 93 WR-01 — callers on module-gated surfaces (Sidebar badge) pass
    // `hasModule('isms')` so gated instances never fire a guaranteed-404
    // fetch on every remount (staleTime: 0). Default `true` preserves the
    // MyTrainingsPage call site.
    enabled,
  });
}

export function useAvailableCourses() {
  return useQuery({
    queryKey: [TRAININGS_KEY, 'available-courses'],
    queryFn: getAvailableCourses,
    // Reads the persisted catalogue (no Dojo call); it only changes when the
    // sync mutation runs, which invalidates this key.
  });
}

/**
 * "Sync from Dojo" — Phase 67 (DJN-04): enqueues an async catalogue sync
 * (MS→N8N→Dojo→MS) and resolves to `{ queued: true }`. The catalogue is no
 * longer returned inline; it arrives later via the inbound receiver, so we
 * only invalidate the available-courses query (the next refetch picks up the
 * receiver's upsert). The page surfaces a "sync queued" signal from
 * `isSuccess`.
 */
export function useSyncCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: syncCatalog,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TRAININGS_KEY, 'available-courses'] });
    },
  });
}

export function useTrainingComplianceMatrix(
  courseSlug: string,
  page = 1,
  pageSize = 10,
  options: { search?: string; status?: ComplianceStatusFilter } = {},
) {
  const { search, status } = options;
  return useQuery({
    queryKey: [TRAININGS_KEY, 'compliance', courseSlug, page, pageSize, search ?? '', status ?? 'all'],
    queryFn: () =>
      getTrainingComplianceMatrix({ courseSlug, page, pageSize, search, status }),
    enabled: Boolean(courseSlug),
    // Live operational board — overrides the app's 5-min default so revisiting the
    // page (or returning to a previously-fetched page size) refetches current
    // compliance state (changes via Dojo passes + the hourly reconcile).
    staleTime: 0,
  });
}

export function useTrainingRequirements() {
  return useQuery({
    queryKey: [TRAININGS_KEY, 'requirements'],
    queryFn: listRequirements,
  });
}

export function useCreateRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RequirementCreateInput) => createRequirement(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TRAININGS_KEY, 'requirements'] });
    },
  });
}

export function useUpdateRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: RequirementUpdateInput }) =>
      updateRequirement(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TRAININGS_KEY, 'requirements'] });
    },
  });
}

export function useDeleteRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteRequirement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TRAININGS_KEY, 'requirements'] });
    },
  });
}

/** Downloads the evidence PDF and hands the blob to the browser on success. */
export function useDownloadTrainingEvidencePdf() {
  return useMutation({
    mutationFn: (params: TrainingEvidencePdfParams) => downloadTrainingEvidencePdf(params),
    onSuccess: ({ blob, filename }, params) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? `training-evidence-${params.variant}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
